// Minimal browser-wallet helper for $WANK payouts. Uses the injected EIP-1193
// provider (MetaMask, or a Safe via WalletConnect-injected provider) directly,
// so no web3 library is added to the bundle. The treasury key never leaves the
// admin's wallet; the backend independently verifies the resulting tx on-chain.

const ERC20_TRANSFER_SELECTOR = "a9059cbb"; // keccak256("transfer(address,uint256)")[:4]

export function hasWallet() {
  return typeof window !== "undefined" && !!window.ethereum;
}

function provider() {
  if (!hasWallet()) throw new Error("No wallet detected. Install MetaMask.");
  return window.ethereum;
}

// Encode ERC-20 transfer(to, amount) calldata. amountBaseUnits is a BigInt.
export function encodeTransfer(to, amountBaseUnits) {
  const addr = String(to).toLowerCase().replace(/^0x/, "").padStart(64, "0");
  const amt = amountBaseUnits.toString(16).padStart(64, "0");
  if (addr.length !== 64 || amt.length > 64) throw new Error("encode-overflow");
  return "0x" + ERC20_TRANSFER_SELECTOR + addr + amt;
}

// Points are 1:1 with the token; scale by token decimals to base units.
export function toBaseUnits(amount, decimals) {
  const whole = BigInt(Math.trunc(Number(amount)));
  return whole * 10n ** BigInt(decimals);
}

export async function connect() {
  const eth = provider();
  const accounts = await eth.request({ method: "eth_requestAccounts" });
  const chainIdHex = await eth.request({ method: "eth_chainId" });
  return { address: accounts[0], chainId: parseInt(chainIdHex, 16) };
}

export async function currentChainId() {
  const hex = await provider().request({ method: "eth_chainId" });
  return parseInt(hex, 16);
}

const toHexChain = (id) => "0x" + Number(id).toString(16);

// Ensure the wallet is on the expected chain; ask it to switch if not. When the
// wallet does not know the chain (error 4902) and an RPC URL is configured, ask
// it to add the chain first. Throws unless the wallet ends up on the chain.
export async function ensureChain(expectedChainId, { chainName, rpcUrl, explorerUrl } = {}) {
  const eth = provider();
  const want = Number(expectedChainId);
  if ((await currentChainId()) === want) return;
  try {
    await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: toHexChain(want) }] });
  } catch (err) {
    const unknownChain = err && (err.code === 4902 || err?.data?.originalError?.code === 4902);
    if (unknownChain && rpcUrl) {
      try {
        await eth.request({
          method: "wallet_addEthereumChain",
          params: [{
            chainId: toHexChain(want),
            chainName: chainName || `Chain ${want}`,
            rpcUrls: [rpcUrl],
            nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
            ...(explorerUrl ? { blockExplorerUrls: [explorerUrl] } : {}),
          }],
        });
      } catch (_) {
        throw new Error(`Wrong network. Switch your wallet to ${chainName || "chain"} (${want}).`);
      }
    } else {
      throw new Error(`Wrong network. Switch your wallet to ${chainName || "chain"} (${want}).`);
    }
  }
  if ((await currentChainId()) !== want) {
    throw new Error(`Wrong network. Switch your wallet to ${chainName || "chain"} (${want}).`);
  }
}

// Send an ERC-20 transfer of `amount` tokens to `to`. Returns the tx hash.
// When `chainId` is given the transfer is refused unless the wallet is on that
// chain at the moment of sending (a wallet can change network between the
// switch prompt and the send prompt).
export async function sendToken({ from, tokenAddress, to, amount, decimals, chainId }) {
  const eth = provider();
  if (chainId !== undefined && chainId !== null) {
    const now = await currentChainId();
    if (now !== Number(chainId)) {
      throw new Error(`Wrong network (wallet is on ${now}, payouts go out on ${chainId}). Nothing was sent.`);
    }
  }
  const data = encodeTransfer(to, toBaseUnits(amount, decimals));
  const txHash = await eth.request({
    method: "eth_sendTransaction",
    params: [{ from, to: tokenAddress, data, value: "0x0" }],
  });
  return txHash;
}

// Back-compat alias for the v1 name (amountByb is the API field name).
export function sendByb({ amountByb, ...rest }) {
  return sendToken({ amount: amountByb, ...rest });
}

// Poll for the receipt until the tx is mined (status known). Resolves the
// receipt or throws on revert / timeout.
export async function waitForReceipt(txHash, { tries = 60, intervalMs = 4000 } = {}) {
  const eth = provider();
  for (let i = 0; i < tries; i += 1) {
    const r = await eth.request({ method: "eth_getTransactionReceipt", params: [txHash] });
    if (r) {
      if (r.status && parseInt(r.status, 16) === 0) {
        const e = new Error("Transaction reverted on-chain.");
        e.reverted = true;
        throw e;
      }
      return r;
    }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  const e = new Error("Timed out waiting for confirmation. Record the tx hash once it confirms.");
  e.timeout = true;
  throw e;
}
